import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Star } from 'lucide-react';
import { Tool } from '../../types';
import Badge from '../ui/Badge';

interface ToolCardProps {
  tool: Tool;
  variant?: 'grid' | 'list';
  featured?: boolean;
  toolLink?: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, variant = 'grid', featured = false, toolLink }) => {
  const isGrid = variant === 'grid';
  var pricePerDay = tool.pricePerDayCents / 100;
  const detailLink = toolLink || `/tool/${tool.id}`;
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
  };
  
  const formatFullDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  return (
    <div 
      className={`
        ${isGrid ? 'flex flex-col h-full' : 'flex flex-col md:flex-row'}
        bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden
        hover:shadow-md transition-shadow duration-200
      `}
    >
      {/* Image */}
      <div 
        className={`
          relative flex-shrink-0
          ${isGrid ? 'w-full pt-[70%]' : 'w-full md:w-40 lg:w-56 h-48 md:h-48'}
        `}
      >
        <Link to={detailLink} className={isGrid ? "absolute inset-0" : "block h-full"}>
          <img 
            src={tool.primaryImageUrl} 
            alt={tool.name}
            className={isGrid ? "absolute inset-0 w-full h-full object-cover" : "w-full h-full object-cover"}
          />
          {featured && (
            <div className="absolute top-2 left-2">
              <Badge variant="featured">Fremhævet</Badge>
            </div>
          )}
        </Link>
      </div>
      
      {/* Content */}
      <div 
        className={`
          flex flex-col flex-grow p-4
          ${isGrid ? '' : 'md:flex-1'}
        `}
      >
        <div className="flex items-start justify-between mb-2">
          <div>
            <Link to={detailLink} className="block">
              <h3 className="text-lg font-medium text-gray-900 hover:text-green-600 transition-colors duration-200">{tool.name}</h3>
            </Link>
            <div className="mt-1 flex items-center text-sm text-gray-500">
              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
              <span>{tool.city}{tool.postalCode ? `, ${tool.postalCode}` : ''}</span>
            </div>
          </div>
          
          <div className="flex items-center">
            <Star className="h-4 w-4 text-yellow-500 mr-1" />
            <span className="text-sm font-medium">{tool.rating || tool.ratingAverage || 0}</span>
            <span className="text-xs text-gray-500 ml-1">({Array.isArray(tool.reviews) ? tool.reviews.length : tool.reviewCount || 0})</span>
          </div>
        </div>
        
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {tool.categories.map((category) => (
            <Badge key={category.id} variant="secondary">
              {category.name}
            </Badge>
          ))}
        </div>
        
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{tool.description}</p>
        
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          {(tool.nextAvailableDate || tool.availability) && (
            <div className="flex items-center text-sm text-gray-500">
              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
              {tool.nextAvailableDate ? (
                <span title={`Tilgængelig fra ${formatFullDate(tool.nextAvailableDate)}`}>
                  {formatDate(tool.nextAvailableDate)}
                </span>
              ) : (
                <span>{tool.availability}</span>
              )}
            </div>
          )}
          
          <span className="font-medium text-green-600 ml-auto">
            {pricePerDay} kr./dag
          </span>
        </div>
      </div>
    </div>
  );
};

export default ToolCard;